/**
 * Authentication API functions
 * Feature 037: Added verifySso for SSO JIT Provisioning
 */

import { apiClient } from '@/core/api/client';
import type { UserRole } from '@/core/auth/types';

export type AuthMethod = 'otp' | 'sso';

export type AuthDiscoverRequest = {
  email: string;
  companySlug?: string;
};

export type AuthDiscoverResponse = {
  companySlug: string | null;
  companyName: string | null;
  companyLogoUrl: string | null;
  userRole: UserRole | null;
  authMethods: AuthMethod[];
  requireSso: boolean;
  allowOtp: boolean;
  isCompanyContextKnown: boolean;
  isEnforcedByCompany: boolean;
  reason:
    | 'company_route'
    | 'existing_user_company'
    | 'care_seeker_exempt'
    | 'fallback_otp';
  kindeOrgId: string | null;
  kindeConnectionId: string | null;
};

/**
 * SSO User data returned from verify-sso endpoint (T037)
 */
export type SsoUser = {
  id: string;
  kindeId?: string | null;
  email: string;
  firstName: string;
  lastName?: string | null;
  role: string;
  partnerCompanyId?: string | null;
  accountStatus: string;
};

/**
 * Partner company summary in SSO response
 */
export type PartnerCompanySummary = {
  id: string;
  name: string;
  logoUrl?: string | null;
};

/**
 * Request payload for SSO verification (T037)
 */
export type VerifySsoRequest = {
  accessToken: string;
  idToken: string;
  expectedCompanySlug?: string | null;
};

/**
 * Response from SSO verification (T037)
 */
export type VerifySsoResponse = {
  user: SsoUser;
  isNewUser: boolean;
  partnerCompany: PartnerCompanySummary | null;
  claimsSynced?: boolean;
};

type CreatePendingUserRequest = {
  email: string;
  role: UserRole;
};

type PendingUserResponse = {
  id: string;
  email: string;
  role: UserRole;
};

export type PublicSignupRequest = {
  firstName: string;
  lastName: string;
  email: string;
  recaptchaToken: string;
};

export type PublicSignupResponse = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};

export type EmailAvailabilityResponse = {
  exists: boolean;
};

type UserProfile = {
  id: string;
  email: string;
  kindeId: string;
  name: string;
  role: string;
  onboardingCompleted?: boolean;
  profileCompleted?: boolean;
  accountStatus?: string;
  vettingStatus?: string;
};

type SyncUserClaimsRequest = {
  kindeId: string;
};

type SyncUserClaimsResponse = {
  id: string;
  email: string;
  role: string;
  onboardingCompleted: boolean;
  claimsSynced: boolean;
};

/**
 * Create a pending user record before Kinde registration
 * Step 2 of 4-step registration flow (FR-001b)
 */
export const createPendingUser = async (
  data: CreatePendingUserRequest,
): Promise<PendingUserResponse> => {
  const response = await apiClient.post<PendingUserResponse>(
    '/auth/pending-user',
    data,
  );
  return response.data;
};

export const publicSignup = async (
  data: PublicSignupRequest,
): Promise<PublicSignupResponse> => {
  const response = await apiClient.post<PublicSignupResponse>(
    '/public-signup',
    data,
  );
  return response.data;
};

export const checkEmailAvailability = async (
  email: string,
): Promise<EmailAvailabilityResponse> => {
  const response = await apiClient.get<EmailAvailabilityResponse>(
    `/users/check-email/${encodeURIComponent(email)}`,
  );
  return response.data;
};

/**
 * Fetch current user profile from database
 * This gets fresh data directly from the database, not cached JWT claims
 */
export const getCurrentUserProfile = async (): Promise<UserProfile> => {
  const response = await apiClient.get<UserProfile>('/auth/me');
  return response.data;
};

/**
 * Sync user custom claims to Kinde on login
 * Ensures role, onboarding status, and other claims are up-to-date in Kinde IDP
 * Called every time an existing user logs in
 */
export const syncUserClaims = async (
  data: SyncUserClaimsRequest,
): Promise<SyncUserClaimsResponse> => {
  const response = await apiClient.post<SyncUserClaimsResponse>(
    '/auth/sync-user',
    data,
  );
  return response.data;
};

export const discoverAuth = async (
  data: AuthDiscoverRequest,
): Promise<AuthDiscoverResponse> => {
  const response = await apiClient.post<AuthDiscoverResponse>('/auth/discover', data);
  return response.data;
};

/**
 * Verify SSO token and provision user if needed (T037 - Feature 037)
 *
 * This endpoint handles SSO authentication with JIT (Just-In-Time) user provisioning:
 * - Validates Kinde SSO tokens (accessToken and idToken)
 * - For new users: Creates user account with partner company assignment
 * - For existing users: Returns user data and links SSO identity if needed
 *
 * @param data - Access and ID tokens from Kinde SSO authentication
 * @returns User data, isNewUser flag, and partner company details
 * @throws 401 if org_code missing or partner company not found
 */
export const verifySso = async (
  data: VerifySsoRequest,
): Promise<VerifySsoResponse> => {
  const response = await apiClient.post<VerifySsoResponse>(
    '/auth/verify-sso',
    data,
  );
  return response.data;
};
